// Генератор пароля «Придумать надёжный пароль» (035: FR-019 – FR-021; research Р4). Пароль
// создаётся в браузере и уходит на сервер только по «Задать пароль». Длина — не меньше 16 знаков
// и не меньше длины из настроек; есть буква, цифра и знак (правило «цифра или символ» сервера);
// алфавит без похожих знаков 0, O, 1, l, I. Случайность — crypto.getRandomValues с отбрасыванием остатка:
// без смещения к первым знакам алфавита. Часть почты до «@» от 4 знаков в пароле не встречается —
// как правило сервера passwordProblem (access/passwords.ts).
// Правка 0.27.4 (приёмка тестового стенда 02.11.2026, ошибка 2; FR-019, FR-024): буква, цифра
// и знак берутся по одному обязательно, остальные — из общего алфавита, затем перемешивание.
// Так все три группы есть при любом потоке байтов, на котором выборка завершается, а не только
// с высокой вероятностью: прежде поток, дававший одни цифры, давал «-222222222222222».

/** Длина пароля, если настройка требует меньше. */
export const GENERATED_PASSWORD_MIN = 16;

const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LETTERS = LOWER + UPPER;
const DIGITS = '23456789';
const SYMBOLS = '-_.!?#%+=';
const ALL = LETTERS + DIGITS + SYMBOLS;

/** Часть почты до «@» короче этого не ищется внутри пароля — как на сервере. */
const LOCAL_PART_MIN = 4;

/** Источник случайных байтов: заполняет массив и возвращает его. */
export type RandomBytes = (bytes: Uint8Array<ArrayBuffer>) => Uint8Array;

export interface GeneratePasswordOptions {
  /** Длина из настроек сервера (auth.password.minLength). */
  readonly minLength: number;
  /** Почта пользователя, если известна целиком: её часть до «@» не попадёт в пароль. */
  readonly email?: string;
  /** Источник случайности; по умолчанию crypto.getRandomValues. */
  readonly random?: RandomBytes;
}

function defaultRandom(bytes: Uint8Array<ArrayBuffer>): Uint8Array {
  return globalThis.crypto.getRandomValues(bytes);
}

/** Случайный индекс 0…size-1 без смещения: байты за последним полным кругом отбрасываются. */
function randomIndex(size: number, random: RandomBytes): number {
  const limit = 256 - (256 % size);
  const byte = new Uint8Array(1);
  for (;;) {
    const value = random(byte)[0]!;
    if (value < limit) return value % size;
  }
}

function pick(alphabet: string, random: RandomBytes): string {
  return alphabet[randomIndex(alphabet.length, random)]!;
}

function attempt(length: number, random: RandomBytes): string {
  // Цифра, знак, буква — по одному обязательно; порядок выборки прежний для цифры и знака.
  const chars = [pick(DIGITS, random), pick(SYMBOLS, random), pick(LETTERS, random)];
  while (chars.length < length) chars.push(pick(ALL, random));
  // Перемешивание Фишера — Йетса: обязательные знаки не стоят всегда первыми.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1, random);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join('');
}

/**
 * Надёжный пароль по правилам сервера: длина max(16, minLength), буква, цифра, знак, без части
 * почты.
 */
export function generatePassword(options: GeneratePasswordOptions): string {
  const random = options.random ?? defaultRandom;
  const length = Math.max(GENERATED_PASSWORD_MIN, Math.floor(options.minLength));
  const local = (options.email ?? '').trim().toLowerCase().split('@', 1)[0] ?? '';
  for (;;) {
    const password = attempt(length, random);
    if (local.length < LOCAL_PART_MIN || !password.toLowerCase().includes(local)) return password;
  }
}
